'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { Feature4 } from '@/components/sections/feature4';
import {
  BubbleAttachment,
  BubbleQuickReplies,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'Channels',
    title: "Your team's AI knowledge brain",
    subtitleA: '@mention it in any channel — instant answers from your',
    subtitleB: 'wiki, docs and past tickets. No more “does anyone know…”.',
    b1: '@open how do we handle EU refund disputes?',
    insight: 'Found in #ops-wiki · last edited 4 days ago',
    b2: '@open what was the resolution on ticket #4521?',
    optOpenTicket: 'Open ticket',
    optOpenThread: 'Open thread',
    optSummarize: 'Summarize',
  },
  ar: {
    eyebrow: 'القنوات',
    title: 'عقل فريقك اللي يعرف كل شيء',
    subtitleA: 'منشنه في أي قناة، ويجيك الرد فوراً من',
    subtitleB: 'الويكي والمستندات والتذاكر القديمة. وخلاص من «أحد يعرف…؟».',
    // ⁦…⁩ = LRI/PDI. Without the isolates RTL bidi renders "@open" and
    // "#ops-wiki" with the sigil flipped to the wrong end.
    b1: '⁦@open⁩ كيف نتعامل مع نزاعات الاسترجاع في الاتحاد الأوروبي؟',
    insight: 'لقيته في ⁦#ops-wiki⁩ · آخر تعديل قبل ٤ أيام',
    b2: '⁦@open⁩ وش كان حل تذكرة ⁦#4521⁩؟',
    optOpenTicket: 'افتح التذكرة',
    optOpenThread: 'افتح الموضوع',
    optSummarize: 'لخّص',
  },
} as const;

export default function SlackPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitleA}
            <br />
            {c.subtitleB}
          </>
        }
        videoSrc="/home/features/slack-person.mp4"
        // English unchanged; on /ar fall through to SubpageHero's localized
        // default ("الوكيل الذكي") instead of the untranslated "Open Agent".
        bubbleAgentName={locale === 'ar' ? undefined : 'Open Agent'}
        bubbles={[
          {
            role: 'user',
            text: c.b1,
          },
          {
            role: 'agent',
            widget: (
              <BubbleAttachment
                fileName="eu-refund-policy.md"
                fileType="DOC"
                insight={c.insight}
              />
            ),
          },
          {
            role: 'user',
            text: c.b2,
          },
          {
            role: 'agent',
            widget: (
              <BubbleQuickReplies
                options={[c.optOpenTicket, c.optOpenThread, c.optSummarize]}
                selected={c.optSummarize}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['Slack AI Features']}
        subtitle={t.channels["Your team's AI-powered knowledge assistant"]}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['Just @mention for Instant Answers'],
            description:
              t.channels[
                '@mention Agent 5 anywhere in Slack. Instant answers from your knowledge base, docs, and past conversations.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Private DMs for Sensitive Questions'],
            description:
              t.channels[
                'Team members can ask confidential questions via direct message. No public channel needed.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Learns Your Company Knowledge'],
            description:
              t.channels[
                'Agent 5 indexes your docs, past Slack conversations, and team knowledge. Gets smarter with every interaction.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Works in Every Channel & Thread'],
            description:
              t.channels[
                'Invite Agent 5 to any public or private channel. Works in threads without cluttering the main conversation.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Automated Alerts & Updates'],
            description:
              t.channels[
                'Schedule messages, set up triggers, broadcast announcements. All automated, zero manual work.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-5.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Every Team Member Gets an AI Assistant'],
            description:
              t.channels[
                'Personal AI help for everyone on your team. No more waiting for responses or searching through docs.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-6.svg',
            imagePosition: 'left',
          },
        ]}
      />

      <Feature4
        heading={
          t.channels[
            "Slack AI that amplifies your team's knowledge without disrupting their workflow"
          ]
        }
      />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels[
            'Find answers to common questions about Slack integration.'
          ]
        }
        items={[
          {
            question: t.channels['How do team members ask Agent 5 questions?'],
            answer:
              t.channels[
                '@mention Agent 5 in any channel or message thread. Type your question and get an instant answer.'
              ],
          },
          {
            question: t.channels['Can I add Agent 5 to multiple channels?'],
            answer:
              t.channels[
                'Yes. Invite Agent 5 to any number of public or private channels. Each team can have AI assistance in their workspace.'
              ],
          },
          {
            question: t.channels['Can Agent 5 send automated messages?'],
            answer:
              t.channels[
                'Yes. Schedule messages, set up triggers, and broadcast updates to channels automatically.'
              ],
          },
          {
            question: t.channels['Where does Agent 5 get its knowledge from?'],
            answer:
              t.channels[
                'Agent 5 learns from your company documentation, past Slack conversations, and team knowledge. It improves as your team uses it.'
              ],
          },
          {
            question: t.channels['Can I DM Agent 5 privately?'],
            answer:
              t.channels[
                'Yes. Send private direct messages to Agent 5 for confidential questions without asking in public channels.'
              ],
          },
          {
            question: t.channels['Does it work in message threads?'],
            answer:
              t.channels[
                'Yes. @mention Agent 5 in any thread to get contextual answers without cluttering the main channel.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
